<script lang="ts">
  import { push } from "svelte-spa-router";
  import {
    getWithJwt,
    serverlessRoutes,
    storage,
    translate,
    errorTracker,
  } from "lib";
  import type {
    WinbackClient,
    OpportunityLabel,
    WinbackStatus,
  } from "../../interfaces/Winback";
  import { relativeTime } from "../../lib/relativeTime";
  import { trainerChatWith } from "../../stores/trainerStores";
  import Svg from "../UI/Svg.svelte";

  export let client: WinbackClient;

  // Guards against a second tap while the chat session is being resolved.
  let isOpeningChat = false;

  const goToProfile = (): void => {
    push(`/profile/${client.client_id}`);
  };

  // The Winback payload has no sessionId, so resolve it the same way the
  // profile screen does before handing off to the chat screen.
  const goToChat = async (): Promise<void> => {
    if (isOpeningChat) return;
    isOpeningChat = true;

    try {
      const res = await getWithJwt(
        `${serverlessRoutes.SESSION}/session-with-user/${client.client_id}`,
      );

      $trainerChatWith = {
        clientId: client.client_id,
        clientName: client.name,
        avatarUrl: null,
        sessionId: res?.data?.sessionId,
      };

      await storage.set("trainerChatWith", $trainerChatWith);
      await push("/chat");
    } catch (err) {
      errorTracker.captureError(err as Error | string, "error", {
        context: "winback_client_card",
        action: "open_chat",
        clientId: client.client_id,
      });
    } finally {
      isOpeningChat = false;
    }
  };

  // Opportunity pill styling. Unlike Reengage's risk pill, a HIGH score here
  // is good news (a strong winback target), so High is green, not red.
  const opportunityClasses: Record<OpportunityLabel, string> = {
    High: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200",
    Medium: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200",
    Low: "bg-slate-100 text-slate-600 dark:bg-zinc-700 dark:text-slate-300",
  };

  // Status badge: "expired" lapsed automatically; "archived" was a deliberate
  // trainer action, so it gets a distinct colour to stand out in the list.
  const statusClasses: Record<WinbackStatus, string> = {
    expired: "bg-slate-100 text-slate-600 dark:bg-zinc-700 dark:text-slate-300",
    archived:
      "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200",
  };

  // Status badge text keys — translated at render time so they react to a
  // language switch without requiring a remount.
  const statusLabelKeys: Record<WinbackStatus, string> = {
    expired: "WINBACK_STATUS_EXPIRED",
    archived: "WINBACK_STATUS_ARCHIVED",
  };

  // Opportunity pill label keys mirror the statusLabelKeys pattern.
  const opportunityLabelKeys: Record<OpportunityLabel, string> = {
    High: "WINBACK_OPPORTUNITY_HIGH",
    Medium: "WINBACK_OPPORTUNITY_MEDIUM",
    Low: "WINBACK_OPPORTUNITY_LOW",
  };

  $: pillClass = opportunityClasses[client.opportunity_label] ?? opportunityClasses.Low;
  $: statusClass = statusClasses[client.status] ?? statusClasses.expired;
  $: statusLabel = translate(
    statusLabelKeys[client.status] ?? statusLabelKeys.expired,
  );
  $: opportunityLabel = translate(
    opportunityLabelKeys[client.opportunity_label] ?? opportunityLabelKeys.Low,
  );
</script>

<article
  class="w-full px-4 py-3 border-b border-slate-200 dark:border-zinc-700 text-slate-900 dark:text-slate-100"
>
  <div class="flex items-start gap-2">
    <div class="flex items-center gap-2 flex-wrap min-w-0">
      <span class={`px-2 py-0.5 rounded-full text-xs font-semibold ${pillClass}`}>
        {opportunityLabel}
      </span>
      <span class={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusClass}`}>
        {statusLabel}
      </span>
      <span class="font-semibold text-base break-words">{client.name}</span>
    </div>

    <div class="ml-auto flex items-center gap-1 shrink-0">
      <button
        type="button"
        class="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors"
        aria-label={translate("PROFILE")}
        title={translate("PROFILE")}
        on:click={goToProfile}
      >
        <Svg name="user" size={20} />
      </button>
      <button
        type="button"
        class="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
        aria-label={translate("CHAT")}
        title={translate("CHAT")}
        disabled={isOpeningChat}
        on:click={goToChat}
      >
        <Svg name="message" size={20} />
      </button>
    </div>
  </div>

  <div class="mt-0.5 text-sm font-medium text-slate-600 dark:text-slate-300">
    {translate("SCORE")}: {client.winback_score}
  </div>

  <dl class="mt-2 text-xs text-slate-600 dark:text-slate-400 space-y-1">
    <div class="flex justify-between gap-3">
      <dt>{translate("WINBACK_LAPSED")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.lapsed_at)}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("WINBACK_USUAL_WORKOUTS_BEFORE_LAPSE")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {client.pre_lapse_weekly_workouts}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_WORKOUT")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_workout_completed_at)}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_SEEN_ONLINE")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_seen_online_at)}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_CLIENT_MESSAGE")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_client_message_at)}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_TRAINER_RESPONSE")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_trainer_response_at)}
      </dd>
    </div>
    {#if client.last_checkin_at}
      <div class="flex justify-between gap-3">
        <dt>{translate("LAST_CHECKIN")}</dt>
        <dd class="text-right text-slate-800 dark:text-slate-200">
          {relativeTime(client.last_checkin_at)}
        </dd>
      </div>
    {/if}
    {#if client.member_since}
      <div class="flex justify-between gap-3">
        <dt>{translate("WINBACK_CLIENT_SINCE")}</dt>
        <dd class="text-right text-slate-800 dark:text-slate-200">
          {relativeTime(client.member_since)}
        </dd>
      </div>
    {/if}
  </dl>

  <p class="mt-2 text-xs text-slate-700 dark:text-slate-300">
    <span class="font-semibold">{translate("REASON")}:</span>
    {client.reason}
  </p>
</article>
